import { useState } from 'react';
import { WIDGET_COLORS } from '../../constants/colors';
import {
  formatPoints,
  formatCurrency,
  progressPercent,
  downPaymentPercent,
  formatContactMonth,
  getTopupSuggestion,
} from '../../utils/formatSavingsPoints';
import type { SavingsPointsData } from '../../hooks/useGetSavingsPoints';
import { WidgetCardHeader } from '../widgetCardHeader';
import { WidgetDivider } from '../widgetDivider';
import { ProgressItem } from '../progressItem';
import { StatRow } from '../statRow';
import { InfoButton } from '../infoButton';
import { EligibilityInfoModal } from '../eligibilityInfoModal';
import { PointsBreakdownLegend } from '../pointsBreakdownLegend';
import { RateChip } from '../rateChip';
import { TopupChip } from '../topupChip';
import { DownPaymentTooltip } from '../downPaymentTooltip';

type Props = { data: SavingsPointsData };

export function BuildingPoints({ data }: Props) {
  const [infoOpen, setInfoOpen] = useState(false);

  const investmentNum = parseFloat(data.pfhpp_investment) || 0;
  const additionalNum = parseFloat(data.additional_downpayment_needed);
  const dpTarget = (investmentNum + additionalNum).toString();
  const pointsPercent = progressPercent(data.savings_points_earned, data.savings_points_required);
  const dpPercent = downPaymentPercent(investmentNum.toString(), dpTarget);

  const bonus = parseFloat(data.bonus_credits_points) || 0;
  const referral = parseFloat(data.referral_credits_points) || 0;
  const base = Math.max(0, parseFloat(data.savings_points_earned) - bonus - referral);
  const hasBonus = bonus > 0;
  const hasReferral = referral > 0;
  const pointsSegments =
    hasBonus || hasReferral
      ? [
          { flex: base || 1, color: WIDGET_COLORS.dustBlue },
          ...(hasBonus ? [{ flex: bonus, color: WIDGET_COLORS.bonus.bar }] : []),
          ...(hasReferral ? [{ flex: referral, color: WIDGET_COLORS.referral.bar }] : []),
        ]
      : [{ flex: 1, color: WIDGET_COLORS.dustBlue }];

  const topup = getTopupSuggestion(data);
  const hasPropertyInfo = !!data.property_value && !!data.deposit_percentage;

  return (
    <>
      <WidgetCardHeader
        variant="default"
        badgeText="In Progress"
        description="Your savings are helping other families purchase their own homes while building towards purchasing your own."
      />

      <WidgetDivider />

      <ProgressItem
        label="Community Points"
        value={
          <span className="text-gray-500">
            <strong className="text-gray-800">{formatPoints(data.savings_points_earned)}</strong> of{' '}
            {formatPoints(data.savings_points_required)} required
          </span>
        }
        barPercent={pointsPercent}
        segments={pointsSegments}
        footer={
          <PointsBreakdownLegend
            basePoints={base.toString()}
            bonusPoints={data.bonus_credits_points}
            referralPoints={data.referral_credits_points}
          />
        }
      />

      <ProgressItem
        label="Down Payment (GYS Home balance)"
        value={
          hasPropertyInfo ? (
            <DownPaymentTooltip
              propertyValue={data.property_value!}
              depositPercentage={data.deposit_percentage!}
              targetAmount={dpTarget}
            >
              <span className="text-gray-500">
                <strong className="text-gray-800">{formatCurrency(investmentNum.toString())}</strong> of{' '}
                {formatCurrency(dpTarget)} target
              </span>
            </DownPaymentTooltip>
          ) : (
            <span className="text-gray-500">
              <strong className="text-gray-800">{formatCurrency(investmentNum.toString())}</strong> of{' '}
              {formatCurrency(dpTarget)} target
            </span>
          )
        }
        barPercent={dpPercent}
        segments={[{ flex: 1, color: WIDGET_COLORS.salmon }]}
      />

      <StatRow
        left={{
          value: `${formatPoints(data.savings_points_earned)} pts`,
          valueColor: WIDGET_COLORS.primary,
          label: 'Community Points',
          extra: <RateChip totalInvestment={data.total_investment} />,
        }}
        right={{
          value: data.eligibility_month ? formatContactMonth(data.eligibility_month) : '-',
          valueColor: WIDGET_COLORS.primary,
          label: (
            <span className="inline-flex items-center uppercase">
              Estimated Eligibility
              <InfoButton onClick={() => setInfoOpen(true)} />
            </span>
          ),
          extra: topup ? <TopupChip suggestion={topup} /> : undefined,
        }}
      />

      <EligibilityInfoModal open={infoOpen} onOpenChange={setInfoOpen} />
    </>
  );
}
